/**
 * RemindersListController — page /relances.html (EPIC-013 / 13.7).
 *
 * Liste paginée des relances de l'org. Filtres statut / période / client /
 * facture. Pagination 25/page.
 *
 * Indicateurs visuels :
 *  - statut `failed` → pill rouge
 *  - statut `scheduled` → pill ocre
 *  - statut `sent` → pill olive
 *  - statut `cancelled` → pill neutre + ligne grisée
 *
 * Lien direct vers la fiche facture concernée (Voir facture →).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const STATUS_LABELS = {
  scheduled: "programmée",
  sent: "envoyée",
  failed: "en échec",
  cancelled: "annulée",
};

const STATUS_PILL_MODIFIER = {
  scheduled: "nm-pill--ochre",
  sent: "nm-pill--olive",
  failed: "nm-pill--red",
  cancelled: "",
};

const VARIANT_LABELS = {
  reminder_b2b_impaye: "Impayé B2B",
  reminder_b2b_partiel: "Partiel B2B",
  reminder_b2c_impaye: "Impayé B2C",
  reminder_b2c_partiel: "Partiel B2C",
};

export default class RemindersListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "loadingRow",
    "summary",
    "statusFilter",
    "periodFilter",
    "clientFilter",
    "invoiceFilter",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 25 },
    statusFilter: { type: String, default: "" },
    fromFilter: { type: String, default: "" },
    toFilter: { type: String, default: "" },
    clientQuery: { type: String, default: "" },
    invoiceQuery: { type: String, default: "" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._restoreFromQueryString();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this.pageValue = page;
    if (params.has("status")) {
      this.statusFilterValue = params.get("status") ?? "";
      if (this.hasStatusFilterTarget) this.statusFilterTarget.value = this.statusFilterValue;
    }
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    if (this.statusFilterValue) params.set("status", this.statusFilterValue);
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState(null, "", newUrl);
  }

  changeStatus(event) {
    this.statusFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changePeriod(event) {
    const value = event.target.value ?? "";
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let from = "";
    let to = "";
    if (value === "last_30d") {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      from = fmt(d);
      to = fmt(today);
    } else if (value === "this_month") {
      from = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
      to = fmt(today);
    } else if (value === "this_year") {
      from = fmt(new Date(today.getFullYear(), 0, 1));
      to = fmt(today);
    }
    this.fromFilterValue = from;
    this.toFilterValue = to;
    this.pageValue = 1;
    this.refresh();
  }

  searchClient(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.clientQueryValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  searchInvoice(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.invoiceQueryValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    this.statusFilterValue = "";
    this.fromFilterValue = "";
    this.toFilterValue = "";
    this.clientQueryValue = "";
    this.invoiceQueryValue = "";
    this.pageValue = 1;
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "";
    if (this.hasPeriodFilterTarget) this.periodFilterTarget.value = "";
    if (this.hasClientFilterTarget) this.clientFilterTarget.value = "";
    if (this.hasInvoiceFilterTarget) this.invoiceFilterTarget.value = "";
    this.refresh();
  }

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");
    this._syncQueryString();

    const params = { page: this.pageValue, pageSize: this.pageSizeValue };
    if (this.statusFilterValue) params.status = this.statusFilterValue;
    if (this.fromFilterValue) params.from = this.fromFilterValue;
    if (this.toFilterValue) params.to = this.toFilterValue;
    // Le backend filtre `clientId` et `invoiceId` par UUID — pour la
    // recherche libre par nom/numéro, V1 reste un filtre client/serveur
    // simple : on n'envoie que si une vraie UUID est saisie. Stub
    // raisonnable, on logge si non vide pour debug.

    try {
      const response = await api.listReminders(params);
      if (seq !== this._fetchSeq) return;
      const items = Array.isArray(response?.items) ? response.items : [];
      const pagination = response?.pagination ?? { page: 1, pageSize: this.pageSizeValue, total: 0 };
      this._renderRows(items);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les relances.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(items) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (items.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-reminder-empty"));
      return;
    }

    for (const reminder of items) {
      const fragment = this.cloneTemplate("tpl-reminder-row");
      const invoiceId = reminder.invoiceId ?? "";
      const clientId = reminder.clientId ?? "";
      const invoiceUrl = invoiceId
        ? `/facture-detail.html?id=${encodeURIComponent(invoiceId)}`
        : "#";
      const clientUrl = clientId
        ? `/clients-detail.html?id=${encodeURIComponent(clientId)}`
        : "#";
      const dateRef = reminder.sentAt ?? reminder.scheduledAt ?? reminder.createdAt ?? null;

      this.fillSlots(fragment, {
        date: dateRef ? this.formatShortDate(dateRef) : "—",
        invoiceNumber: reminder.invoiceNumber ?? "—",
        clientName: reminder.clientLegalName ?? "—",
        rank: reminder.rank ? `#${reminder.rank}` : "—",
        variantLabel: VARIANT_LABELS[reminder.variant] ?? (reminder.variant ?? "—"),
        variantSub: this._variantSubLabel(reminder),
        remaining: this._formatAmount(reminder.amountRemaining),
        statusLabel: STATUS_LABELS[reminder.status] ?? reminder.status,
      });

      const dateEl = fragment.querySelector('[data-slot-attr-datetime="dateIso"]');
      if (dateEl && dateRef) dateEl.setAttribute("datetime", dateRef);

      const invoiceLink = fragment.querySelector('[data-slot-attr-href="invoiceUrl"]');
      if (invoiceLink) invoiceLink.setAttribute("href", invoiceUrl);
      const clientLink = fragment.querySelector('[data-slot-attr-href="clientUrl"]');
      if (clientLink) {
        if (clientUrl === "#") {
          clientLink.removeAttribute("href");
        } else {
          clientLink.setAttribute("href", clientUrl);
        }
      }
      const actionLink = fragment.querySelector('[data-slot-attr-href="actionUrl"]');
      if (actionLink) {
        actionLink.setAttribute("href", invoiceUrl);
        actionLink.setAttribute(
          "aria-label",
          `Voir la facture ${reminder.invoiceNumber ?? invoiceId}`,
        );
      }

      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const modifier = STATUS_PILL_MODIFIER[reminder.status];
      if (pill && modifier) pill.classList.add(modifier);
      if (reminder.status === "cancelled") {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _variantSubLabel(reminder) {
    if (reminder.status === "sent" && reminder.deliveryStatus) return reminder.deliveryStatus;
    if (reminder.status === "scheduled") return "prévue";
    if (reminder.status === "failed" && reminder.failureReason) return reminder.failureReason;
    return "";
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    this.summaryTarget.textContent = total === 0
      ? "Aucune relance"
      : `${total} relance${total > 1 ? "s" : ""}`;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const pageSize = Number(pagination.pageSize) || this.pageSizeValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} relances`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token,
        text: String(token),
        label: `Page ${token}`,
        current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-reminder-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return raw;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  }
}
